import dayjs from 'dayjs';
import Duration from 'dayjs/plugin/duration';

import { parseDateTimeIgnoringTz } from './datetimeUtils';

dayjs.extend(Duration);

export function arrayEquals(a, b) {
  return (
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((val, index) => val === b[index])
  );
}

function isObject(val) {
  if (val === null) return false;
  return typeof val === 'function' || typeof val === 'object';
}

export function deepEquals(a, b) {
  if (a === b) {
    return true;
  } else if (Array.isArray(a) && Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((val, index) => deepEquals(val, b[index]))
    );
  } else if (isObject(a) && isObject(b)) {
    return (
      arrayEquals(Object.keys(a), Object.keys(b)) &&
      Object.keys(a).every((val) => deepEquals(a[val], b[val]))
    );
  }
  return false;
}

export function deepClone(target) {
  return JSON.parse(JSON.stringify(target));
}

export const MILES_TO_KM = 1.60934;
export const KM_TO_MILES = 1 / MILES_TO_KM;
export const HH_MM_FORMAT = 'HH:mm';

export function speedUnit(uses_metric: boolean) {
  return uses_metric ? 'kph' : 'mph';
}

export function distanceUnit(uses_metric: boolean) {
  return uses_metric ? 'km' : 'mi';
}

export function convertSpeeds(value: number, uses_metric: boolean) {
  if (!value) {
    return value;
  }
  if (uses_metric) {
    return value * MILES_TO_KM;
  } else {
    return value;
  }
}

export function capitalizeFirstLetter(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function formatSpeed(
  value: string | number,
  uses_metric: boolean,
  value_already_converted: boolean = false,
) {
  const nValue = value_already_converted
    ? Number(value)
    : convertSpeeds(Number(value), uses_metric);
  return `${nValue.toFixed(0)} ${speedUnit(uses_metric)}`;
}

export function formatSpeedKmph(value: number, uses_metric) {
  return formatSpeed(value * KM_TO_MILES, uses_metric);
}

export function formatDistance(value_meters: number, uses_metric: boolean) {
  const value_in_unit = uses_metric
    ? value_meters / 1000
    : value_meters / (1000 * MILES_TO_KM);
  let value_formatted = value_in_unit.toFixed(1);
  if (value_in_unit < 0.2) {
    value_formatted = value_in_unit.toFixed(2);
  }
  return `${value_formatted} ${distanceUnit(uses_metric)}`;
}

export function posMod(v: number, mod: number): number {
  return ((v % mod) + mod) % mod;
}

export function selectorFromId(id: string) {
  if (id.startsWith('#')) {
    return id;
  } else {
    return `#${id}`;
  }
}

export function isCtrlEvent(e: KeyboardEvent) {
  return e.metaKey || e.ctrlKey;
}

export function formatTraveltime(v: number, format?: string): string {
  let f: string;
  if (format) f = format;
  else {
    f = 'H [hrs] m [mins] s[s]';
    if (v < 60) f = 's[s]';
    // if more than 30 minutes, do not show seconds
    else if (v >= 30 * 60 || v % 60 === 0) f = 'H [hrs] m [mins]';
  }
  const duration = dayjs.duration(v, 'seconds');
  return duration
    .format(f)
    .replace(/0 hrs /, '')
    .replace(/1 hrs/, '1 hr') // shouldn't match only the beginning
    .replace(/1 mins/, '1 min')
    .replace(/1 days/, '1 day')
    .replace(/^0 days /, '')
    .replace(/^0 mins /, '');
}
export const endsWithNumberInBracketsReg = / ?\(([0-9]+)\)$/m;

export function getNameNumberIfExists(name: string) {
  const matches = name.match(endsWithNumberInBracketsReg);
  if (matches) {
    const n = parseInt(matches[1], 10);
    return n;
  }
  return undefined;
}

export function getIncrementalName(name: string) {
  const n = getNameNumberIfExists(name);
  if (n) {
    name = name.replace(`(${n})`, `(${n + 1})`);
  } else {
    name = `${name} (2)`;
  }
  return name;
}

export const updateNameError = (
  oldName: undefined | string,
  newName: string,
  existingNames: string[],
  checkEmptyName: boolean = false,
): string | undefined => {
  const trimmedNewName = newName.trim().toLowerCase();
  const trimmedOldName = oldName?.trim().toLowerCase();

  // Returns the error message, or undefined if no error
  if (checkEmptyName && (!newName || trimmedNewName.length === 0))
    return 'Name cannot be empty';

  if (trimmedOldName !== undefined && trimmedOldName === trimmedNewName)
    return undefined; // ok

  if (existingNames.some((val) => trimmedNewName === val?.trim().toLowerCase()))
    return 'Name must be unique';

  return undefined;
};

export const roundToNearestValue = (numb: number, val: number): number => {
  const remainder = numb % val;

  return remainder < val / 2 ? numb - remainder : numb + (val - remainder);
};

export enum SortOrder {
  NONE = 0,
  ASC = 1,
  DESC = -1,
}

export function sortByDate<T extends Record<string, any>>(
  items: T[],
  accessor: keyof T,
  order: SortOrder,
): T[] {
  return [...items].sort((a, b) => {
    const rawA = a[accessor];
    const rawB = b[accessor];

    const dateA = rawA ? parseDateTimeIgnoringTz(rawA as any).valueOf() : null;
    const dateB = rawB ? parseDateTimeIgnoringTz(rawB as any).valueOf() : null;

    if (dateA === null && dateB === null) return 0;
    if (dateA === null) return 1;
    if (dateB === null) return -1;

    return order === SortOrder.ASC ? dateA - dateB : dateB - dateA;
  });
}

// function to join classes conditionally
export const classNames = (
  ...parts: Array<string | false | null | undefined>
) => parts.filter(Boolean).join(' ');
